import type { GridSelection } from "@glideapps/glide-data-grid";
import { type AdaptedSpreadsheetSheet, isCellEditable } from "./adapter";
import type { FormulaBarCommitTarget } from "./formula-bar";
import {
  formatCoordinate,
  getCellErrorCode,
  getEditedCellSelection,
  getFormulaBarValue,
} from "./utils";

interface GetSelectedCellStateParams {
  gridSelection: GridSelection;
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  canMutateActiveSheet: boolean;
}

export const getSelectedCellState = ({
  gridSelection,
  adaptedSheet,
  canMutateActiveSheet,
}: GetSelectedCellStateParams) => {
  const selectedColumnIndex = gridSelection.columns.first();
  const selectedCell = gridSelection.current?.cell ?? null;
  const [selectedCol, selectedRow] = selectedCell ?? [null, null];
  const selectedCellRecord =
    selectedCell && adaptedSheet
      ? adaptedSheet.getCellRecord(selectedCell[0], selectedCell[1])
      : undefined;
  const isNewRowSelected =
    !!selectedCell &&
    !selectedCellRecord &&
    !!adaptedSheet &&
    adaptedSheet.isAddRowCell(selectedCell[0], selectedCell[1]);
  const canEditSelectedCell =
    !!selectedCellRecord &&
    canMutateActiveSheet &&
    isCellEditable(selectedCellRecord);
  const formulaBarCommitTarget: FormulaBarCommitTarget | null =
    canEditSelectedCell && selectedCellRecord
      ? { kind: "cell", cellId: selectedCellRecord.id }
      : canMutateActiveSheet && isNewRowSelected && selectedCell
        ? { kind: "new-row", columnIndex: selectedCell[0] }
        : null;

  const selectedCoordinate = selectedCell
    ? formatCoordinate(
        selectedCellRecord
          ? [selectedCellRecord.col - 1, selectedCellRecord.row - 1]
          : selectedCell,
      )
    : null;

  const formulaBarValue = getFormulaBarValue(selectedCellRecord);
  const selectedCellErrorCode = getCellErrorCode(selectedCellRecord);

  const selectedEditedCell = getEditedCellSelection(selectedCellRecord);

  return {
    selectedColumnIndex,
    selectedCell,
    selectedCol,
    selectedRow,
    selectedCellRecord,
    isNewRowSelected,
    formulaBarCommitTarget,
    selectedCoordinate,
    formulaBarValue,
    selectedCellErrorCode,
    selectedEditedCell,
  };
};
